import { FC, useMemo } from 'react';
import { createStyles, makeStyles } from '@material-ui/core';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { format } from 'date-fns';

import {
  METRICS_INTERVAL_CONFIG,
  METRICS_INTERVALS,
  MetricsInterval,
} from './constants';
import { MetricsChartData, MetricsNullableNumber } from './types';

const useStyles = makeStyles(() =>
  createStyles({
    widget: {
      background: '#0F1216',
      borderRadius: 2,
      overflow: 'hidden',
      minWidth: 0,
      width: '100%',
    },
    toolbar: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 12,
      flexWrap: 'wrap',
      padding: '12px 16px 0',
    },
    chartTitle: {
      color: '#D0D4D8',
      fontSize: 13,
      fontWeight: 400,
      lineHeight: '18px',
    },
    toggleButton: {
      color: '#C8CDD2 !important',
      backgroundColor: '#1A1F24',
      border: '1px solid #2A3138 !important',
      textTransform: 'none',
      fontSize: 11,
      fontWeight: 500,
      padding: '2px 8px',
      height: 26,
    },
    toggleButtonSelected: {
      color: '#FFFFFF !important',
      backgroundColor: '#2E3640',
    },
    chartWrap: {
      '& .highcharts-legend, & .highcharts-credits, & .highcharts-button': {
        display: 'none',
      },
      '& .highcharts-title, & .highcharts-xaxis-labels text, & .highcharts-yaxis-labels text':
        {
          display: 'block',
        },
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: 24,
      padding: '8px 16px 16px',
    },
    stats: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: 12,
      color: '#C8CDD2',
    },
    statsHead: {
      color: '#8B929A',
      textAlign: 'right',
      fontWeight: 400,
      padding: '4px 10px',
    },
    statsName: {
      textAlign: 'left',
      padding: '4px 10px 4px 0',
      whiteSpace: 'nowrap',
      width: '100%',
    },
    statsCell: {
      textAlign: 'right',
      padding: '4px 10px',
      fontVariantNumeric: 'tabular-nums',
    },
    swatch: {
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      marginRight: 8,
    },
  })
);

const formatMetricNumber = (value: MetricsNullableNumber) => {
  if (value === null) {
    return '—';
  }
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  if (Number.isInteger(value)) {
    return String(value);
  }
  return value.toFixed(1);
};

const seriesNeedsMarkers = (values: MetricsNullableNumber[]) => {
  const indexes = values
    .map((value, index) => (value === null ? -1 : index))
    .filter((index) => index >= 0);

  if (indexes.length === 0) {
    return false;
  }

  if (indexes.length === 1) {
    return true;
  }

  return indexes.some(
    (index, position) => position > 0 && index !== indexes[position - 1] + 1
  );
};

const formatAxisTime = (timestamp: number, interval: MetricsInterval) => {
  if (interval === '1w' || interval === '24h') {
    return format(timestamp, 'MMM d');
  }
  if (interval === '6h') {
    return format(timestamp, 'MMM d H:mm');
  }
  return format(timestamp, 'H:mm');
};

interface MetricsGraphProps {
  data: MetricsChartData;
  interval: MetricsInterval;
  onIntervalChange?: (interval: MetricsInterval) => void;
  showIntervalToggle?: boolean;
}

const MetricsGraph: FC<MetricsGraphProps> = ({
  data,
  interval,
  onIntervalChange,
  showIntervalToggle = false,
}) => {
  const classes = useStyles();

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        height: 220,
        spacing: [8, 16, 12, 12],
        style: {
          fontFamily: 'Roboto, Sans Serif',
        },
      },
      title: {
        text: null,
      },
      credits: {
        enabled: false,
      },
      legend: {
        enabled: false,
      },
      xAxis: {
        type: 'datetime',
        tickInterval: data.stepMs || METRICS_INTERVAL_CONFIG[interval].periodMs,
        min: data.timestamps[0],
        max: data.timestamps[data.timestamps.length - 1],
        startOnTick: false,
        endOnTick: false,
        lineColor: '#2A3138',
        tickColor: '#2A3138',
        tickLength: 4,
        gridLineWidth: 0,
        labels: {
          style: { color: '#8B929A', fontSize: '11px' },
          autoRotation:
            interval === '6h' || interval === '24h' || interval === '1w'
              ? [-45]
              : false,
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            return formatAxisTime(Number(this.value), interval);
          },
        },
      },
      yAxis: {
        opposite: false,
        title: { text: null },
        gridLineColor: 'rgba(255, 255, 255, 0.04)',
        labels: {
          style: { color: '#8B929A', fontSize: '11px' },
          formatter(this: Highcharts.AxisLabelsFormatterContextObject) {
            const value = Number(this.value);
            if (Math.abs(value) >= 1000) {
              return `${value / 1000}k`;
            }
            return String(value);
          },
        },
      },
      tooltip: {
        shared: true,
        backgroundColor: '#161B22',
        borderColor: '#2A3138',
        style: { color: '#E6EDF3' },
        xDateFormat: '%b %d, %H:%M',
      },
      plotOptions: {
        line: {
          lineWidth: 2,
          connectNulls: false,
          marker: {
            enabled: false,
            radius: 4,
            symbol: 'circle',
          },
        },
      },
      series: data.series.map((item) => ({
        type: 'line' as const,
        name: item.name,
        color: item.color,
        marker: {
          enabled: seriesNeedsMarkers(item.values),
          radius: 4,
          symbol: 'circle',
        },
        data: data.timestamps.map((timestamp, index) => [
          timestamp,
          item.values[index],
        ]),
      })),
    };
  }, [data.series, data.stepMs, data.timestamps, interval]);

  return (
    <div className={classes.widget}>
      <div className={classes.toolbar}>
        <div className={classes.chartTitle}>{data.title}</div>
        {showIntervalToggle && onIntervalChange && (
          <ToggleButtonGroup
            value={interval}
            exclusive
            size='small'
            onChange={(_, next: MetricsInterval | null) => {
              if (next) {
                onIntervalChange(next);
              }
            }}
            aria-label='Time interval'
          >
            {METRICS_INTERVALS.map((option) => (
              <ToggleButton
                key={option.id}
                value={option.id}
                disableRipple
                className={classes.toggleButton}
                classes={{ selected: classes.toggleButtonSelected }}
              >
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </div>

      <div className={classes.chartWrap}>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </div>

      <div className={classes.footer}>
        <table className={classes.stats}>
          <thead>
            <tr>
              <th className={classes.statsHead} style={{ textAlign: 'left' }}>
                Name
              </th>
              <th className={classes.statsHead}>Mean</th>
              <th className={classes.statsHead}>Last*</th>
              <th className={classes.statsHead}>Max</th>
              <th className={classes.statsHead}>Total</th>
            </tr>
          </thead>
          <tbody>
            {data.series.map((row) => (
              <tr key={row.key}>
                <td className={classes.statsName}>
                  <span
                    className={classes.swatch}
                    style={{ background: row.color }}
                  />
                  {row.name}
                </td>
                <td className={classes.statsCell}>
                  {formatMetricNumber(row.stats.mean)}
                </td>
                <td className={classes.statsCell}>
                  {formatMetricNumber(row.stats.last)}
                </td>
                <td className={classes.statsCell}>
                  {formatMetricNumber(row.stats.max)}
                </td>
                <td className={classes.statsCell}>
                  {formatMetricNumber(row.stats.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MetricsGraph;
